import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, FileText, Briefcase, Award, Building2, Mail, LogOut, 
  DollarSign, TrendingUp, CheckCircle, XCircle, Clock, AlertCircle,
  Download, Filter, Search, Eye, Settings
} from 'lucide-react';
import axios from 'axios';
import ManageLawyers from './admin-tabs/ManageLawyers';
import ManageInsights from './admin-tabs/ManageInsights';
import ManageCaseStudies from './admin-tabs/ManageCaseStudies';
import ManageAwards from './admin-tabs/ManageAwards';
import ManageSectors from './admin-tabs/ManageSectors';
import ManageUsers from './admin-tabs/ManageUsers';
import PageTitle from '../hooks/PageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    users: 0,
    lawyers: 0,
    insights: 0,
    caseStudies: 0,
    awards: 0,
    sectors: 0,
    messages: 0
  });
  const [financialSummary, setFinancialSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        lawyers, insights, caseStudies, awards, sectors, messages,
        financialRes, invoicesRes
      ] = await Promise.all([
        axios.get(`${API_URL}/api/lawyers/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/insights/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/case-studies/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/awards/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sectors/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/contact/`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/financial-summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/invoices/all`, { withCredentials: true })
      ]);

      setStats({
        lawyers: lawyers.data.length,
        insights: insights.data.length,
        caseStudies: caseStudies.data.length,
        awards: awards.data.length,
        sectors: sectors.data.length,
        messages: messages.data.length
      });
      
      setFinancialSummary(financialRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleApproveInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${invoiceId}/approve`, {}, { withCredentials: true });
      await fetchAllData();
    } catch (error) {
      console.error('Error approving invoice:', error);
      alert('Failed to approve invoice');
    }
  };

  const handleRejectInvoice = async (invoiceId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${invoiceId}/reject`, 
        { rejection_reason: reason }, 
        { withCredentials: true }
      );
      await fetchAllData();
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      alert('Failed to reject invoice');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${invoiceId}/mark-paid`, {}, { withCredentials: true });
      await fetchAllData();
    } catch (error) {
      console.error('Error marking as paid:', error);
      alert(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleDownloadInvoice = (invoice) => {
    // Generate simple text invoice
    const invoiceText = `
INVOICE - Ashbourne Law
==========================================

Invoice ID: ${invoice.id}
Solicitor: ${invoice.solicitor_name || invoice.solicitor_id}
Amount: £${invoice.amount?.toFixed(2) || '0.00'}
Split Amount (70%): £${invoice.split_amount?.toFixed(2) || '0.00'}
Status: ${invoice.approval_status}
Created: ${new Date(invoice.created_at).toLocaleDateString('en-GB')}
${invoice.approved_at ? `Approved: ${new Date(invoice.approved_at).toLocaleDateString('en-GB')}` : ''}
${invoice.paid_at ? `Paid: ${new Date(invoice.paid_at).toLocaleDateString('en-GB')}` : ''}

Description:
${invoice.description || 'No description'}

Time Entries:
${invoice.time_entries?.map(entry => `- ${entry.description}: ${entry.hours}h @ £${entry.rate}/h`).join('\n') || 'No time entries'}

==========================================
Ashbourne Law - Professional Legal Services
    `;

    const blob = new Blob([invoiceText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice_${invoice.id}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csvContent = [
      ['Invoice ID', 'Solicitor', 'Amount', 'Split Amount', 'Status', 'Created', 'Approved', 'Paid'].join(','),
      ...filteredInvoices.map(inv => [
        inv.id,
        inv.solicitor_name || inv.solicitor_id,
        inv.amount?.toFixed(2) || '0',
        inv.split_amount?.toFixed(2) || '0',
        inv.approval_status,
        new Date(inv.created_at).toLocaleDateString('en-GB'),
        inv.approved_at ? new Date(inv.approved_at).toLocaleDateString('en-GB') : '',
        inv.paid_at ? new Date(inv.paid_at).toLocaleDateString('en-GB') : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesFilter = filterStatus === 'all' || inv.approval_status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.solicitor_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const statCards = [
    { icon: Users, label: 'Lawyers', value: stats.lawyers, color: 'bg-blue-500' },
    { icon: FileText, label: 'Insights', value: stats.insights, color: 'bg-green-500' },
    { icon: Briefcase, label: 'Case Studies', value: stats.caseStudies, color: 'bg-purple-500' },
    { icon: Award, label: 'Awards', value: stats.awards, color: 'bg-yellow-500' },
    { icon: Building2, label: 'Sectors', value: stats.sectors, color: 'bg-indigo-500' },
    { icon: Mail, label: 'Messages', value: stats.messages, color: 'bg-red-500' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <PageTitle title="Admin Dashboard" />
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif text-gray-900">Dashboard Admin</h1>
              <p className="text-gray-600 mt-1">Welcome, {user?.name}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 mt-6 border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'overview' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'invoices' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Invoices Management
              {financialSummary?.pending_approval_count > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                  {financialSummary.pending_approval_count}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('lawyers')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'lawyers' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="h-4 w-4 inline mr-1" />
              Lawyers
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'users' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="h-4 w-4 inline mr-1" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'insights' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="h-4 w-4 inline mr-1" />
              Blog
            </button>
            <button
              onClick={() => setActiveTab('cases')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'cases' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Briefcase className="h-4 w-4 inline mr-1" />
              Case Studies
            </button>
            <button
              onClick={() => setActiveTab('awards')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'awards' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Award className="h-4 w-4 inline mr-1" />
              Awards
            </button>
            <button
              onClick={() => setActiveTab('sectors')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'sectors' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Building2 className="h-4 w-4 inline mr-1" />
              Sectors
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <>
            {/* Financial Overview */}
            {financialSummary && (
              <div className="mb-8">
                <h2 className="text-2xl font-serif text-gray-900 mb-6">Financial Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign className="h-8 w-8" />
                      <span className="text-sm opacity-90">This Month</span>
                    </div>
                    <p className="text-3xl font-bold">£{financialSummary.this_month_revenue.toLocaleString()}</p>
                    <p className="text-sm mt-1 opacity-90">Revenue mensile</p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="h-8 w-8" />
                      <span className="text-sm opacity-90">This Year</span>
                    </div>
                    <p className="text-3xl font-bold">£{financialSummary.this_year_revenue.toLocaleString()}</p>
                    <p className="text-sm mt-1 opacity-90">Revenue annuale</p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <Clock className="h-8 w-8" />
                      <span className="text-sm opacity-90">Pending</span>
                    </div>
                    <p className="text-3xl font-bold">{financialSummary.pending_approval_count}</p>
                    <p className="text-sm mt-1 opacity-90">Fatture in attesa</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <AlertCircle className="h-8 w-8" />
                      <span className="text-sm opacity-90">Payable</span>
                    </div>
                    <p className="text-3xl font-bold">£{financialSummary.total_payable_to_solicitors.toLocaleString()}</p>
                    <p className="text-sm mt-1 opacity-90">Da pagare ai solicitor</p>
                  </div>
                </div>
              </div>
            )}

            {/* Revenue Split */}
            {financialSummary && (
              <div className="mb-8 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Split (70/30 Model)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-6 shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Solicitor Share (70%)</span>
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-3xl font-bold text-blue-600">
                      £{financialSummary.total_solicitor_share_70.toLocaleString()}
                    </p>
                    <div className="mt-4 bg-blue-100 rounded-full h-3">
                      <div className="bg-blue-600 h-3 rounded-full" style={{ width: '70%' }}></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-6 shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Firm Share (30%)</span>
                      <Building2 className="h-5 w-5 text-gray-600" />
                    </div>
                    <p className="text-3xl font-bold text-gray-600">
                      £{financialSummary.total_firm_share_30.toLocaleString()}
                    </p>
                    <div className="mt-4 bg-gray-200 rounded-full h-3">
                      <div className="bg-gray-600 h-3 rounded-full" style={{ width: '30%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Content Stats */}
            <h2 className="text-2xl font-serif text-gray-900 mb-6">Statistiche Contenuti</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                        <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                      </div>
                      <div className={`${stat.color} p-3 rounded-lg`}>
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick Actions */}
            <h2 className="text-2xl font-serif text-gray-900 mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <button 
                onClick={() => setActiveTab('lawyers')}
                className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-left"
              >
                <Users className="h-8 w-8 text-gray-900 mb-3" />
                <h3 className="font-medium text-gray-900 mb-1">Manage Lawyers</h3>
                <p className="text-sm text-gray-600">Add or edit lawyer profiles</p>
              </button>
              <button 
                onClick={() => setActiveTab('insights')}
                className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-left"
              >
                <FileText className="h-8 w-8 text-gray-900 mb-3" />
                <h3 className="font-medium text-gray-900 mb-1">Publish Insights</h3>
                <p className="text-sm text-gray-600">Create new blog articles</p>
              </button>
              <button 
                onClick={() => setActiveTab('messages')}
                className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow text-left"
              >
                <Mail className="h-8 w-8 text-gray-900 mb-3" />
                <h3 className="font-medium text-gray-900 mb-1">Messages</h3>
                <p className="text-sm text-gray-600">View messages from contact form</p>
              </button>
            </div>
          </>
        )}

        {activeTab === 'invoices' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-serif text-gray-900">Solicitor Invoice Management</h2>
                <p className="text-gray-600 mt-1">{filteredInvoices.length} invoices found</p>
              </div>
              <button 
                onClick={handleExportCSV}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <Download className="h-5 w-5" />
                <span>Export CSV</span>
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Filter className="h-4 w-4 inline mr-1" />
                    Filtr by Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="all">Tutte</option>
                    <option value="pending_approval">⏳ Pending Approval</option>
                    <option value="approved">✓ Approved</option>
                    <option value="rejected">✗ Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search className="h-4 w-4 inline mr-1" />
                    Search
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Invoice # o Solicitor name..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Invoices Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Solicitor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">70% / 30%</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approval</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                          <div className="text-xs text-gray-500">{new Date(invoice.issue_date).toLocaleDateString('it-IT')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{invoice.solicitor_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{invoice.solicitor_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.client_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.case_title || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">£{invoice.gross_total.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-blue-600 font-medium">£{invoice.solicitor_share.toLocaleString()}</div>
                          <div className="text-xs text-gray-500">£{invoice.firm_share.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            invoice.approval_status === 'pending_approval' ? 'bg-orange-100 text-orange-800' :
                            invoice.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {invoice.approval_status === 'pending_approval' ? '⏳ Pending' :
                             invoice.approval_status === 'approved' ? '✓ Approved' :
                             '✗ Rejected'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {invoice.approval_status === 'pending_approval' && (
                              <>
                                <button
                                  onClick={() => handleApproveInvoice(invoice.id)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Approve"
                                >
                                  <CheckCircle className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleRejectInvoice(invoice.id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Reject"
                                >
                                  <XCircle className="h-5 w-5" />
                                </button>
                              </>
                            )}
                            {invoice.approval_status === 'approved' && invoice.status !== 'paid' && (
                              <button
                                onClick={() => handleMarkPaid(invoice.id)}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                              >
                                Mark Paid
                              </button>
                            )}
                            {invoice.status === 'paid' && (
                              <span className="text-xs text-green-600 font-medium">✓ Paid</span>
                            )}
                            <button
                              onClick={() => handleDownloadInvoice(invoice)}
                              className="text-gray-600 hover:text-gray-900 ml-2"
                              title="Download Invoice"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredInvoices.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No invoices found</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Lawyers Tab */}
        {activeTab === 'lawyers' && <ManageLawyers />}

        {/* Users Tab */}
        {activeTab === 'users' && <ManageUsers />}

        {/* Insights Tab */}
        {activeTab === 'insights' && <ManageInsights />}

        {/* Case Studies Tab */}
        {activeTab === 'cases' && <ManageCaseStudies />}

        {/* Awards Tab */}
        {activeTab === 'awards' && <ManageAwards />}

        {/* Sectors Tab */}
        {activeTab === 'sectors' && <ManageSectors />}
      </div>
    </div>
  );
};

export default AdminDashboard;
